import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://kenandbryn.dance';
const SOCIAL_IMAGE = 'https://aqgeoubahtnbzgfglgky.supabase.co/storage/v1/object/public/images/GGP2022_SideBySide.webp';

const pageMetadata = {
  '/': {
    title: 'Ken & Bryn | West Coast Swing Dancers & Teachers',
    description: 'Follow West Coast Swing champions Ken and Bryn for events, teaching, performances, and members-only dance content.',
  },
  '/about': {
    title: 'About Ken & Bryn | West Coast Swing Champions',
    description: 'Meet Ken and Bryn, internationally recognized West Coast Swing champions, performers, and teachers.',
  },
  '/events': {
    title: 'Upcoming Events | Ken & Bryn',
    description: 'Find upcoming events where Ken and Bryn are teaching, competing, and performing West Coast Swing around the world.',
  },
  '/presskit': {
    title: 'Press Kit | Ken & Bryn',
    description: 'Download approved photos and biographies for Ken and Bryn event pages, programs, and announcements.',
  },
  '/media-kit': {
    title: 'Partnership Media Kit | Ken & Bryn',
    description: 'Explore Ken and Bryn’s current audience reach, demographics, brand fit, and partnership opportunities, or download their media kit.',
  },
  '/contact': {
    title: 'Contact Ken & Bryn | Bookings, Lessons & Events',
    description: 'Contact Ken and Bryn about bookings, lessons, events, and members-only dance content.',
  },
  '/lessons': {
    title: 'Request a Private Lesson | Ken & Bryn',
    description: 'Request a 45-minute private dance lesson with Ken, Bryn, or both at an upcoming event or locally when they are in town.',
  },
  '/403': {
    title: '403 Access Denied | Ken & Bryn',
    description: 'You do not have permission to view the requested page.',
    noIndex: true,
  },
  '/500': {
    title: '500 Server Error | Ken & Bryn',
    description: 'Something unexpected happened while loading the requested page.',
    noIndex: true,
  },
  '/503': {
    title: '503 Temporarily Unavailable | Ken & Bryn',
    description: 'The website is temporarily unavailable.',
    noIndex: true,
  },
  '/admin': {
    title: 'Website Admin | Ken & Bryn',
    description: 'Authorized website content management.',
    noIndex: true,
  },
};

const setMeta = (selector, attribute, value) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    const propertyMatch = selector.match(/meta\[property="([^"]+)"\]/);
    const nameMatch = selector.match(/meta\[name="([^"]+)"\]/);
    if (propertyMatch) element.setAttribute('property', propertyMatch[1]);
    if (nameMatch) element.setAttribute('name', nameMatch[1]);
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, value);
};

const siteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Ken & Bryn',
  url: SITE_URL,
  image: SOCIAL_IMAGE,
  sameAs: [
    'https://www.youtube.com/channel/UCDPPfrCYMXon-KCSRfKymTQ',
    'https://www.instagram.com/kenandbryn/',
    'https://www.tiktok.com/@kenandbryn',
    'https://www.facebook.com/kenandbryn/',
  ],
};

export default function RouteMetadata() {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath = pathname === '/home' ? '/' : pathname.replace(/\/$/, '') || '/';
    const metadata = pageMetadata[normalizedPath]
      || (normalizedPath.startsWith('/admin/') ? pageMetadata['/admin'] : undefined);
    const isErrorPage = !metadata || metadata.noIndex;
    const canonicalUrl = `${SITE_URL}${normalizedPath === '/' ? '' : normalizedPath}`;
    const title = metadata?.title || 'Page Not Found | Ken & Bryn';
    const description = metadata?.description || 'The requested page could not be found.';

    document.title = title;
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="robots"]', 'content', isErrorPage ? 'noindex, nofollow' : 'index, follow');
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    setMeta('meta[property="og:image"]', 'content', SOCIAL_IMAGE);
    setMeta('meta[property="og:image:alt"]', 'content', 'Ken and Bryn dancing West Coast Swing');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', SOCIAL_IMAGE);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    let structuredData = document.head.querySelector('#site-structured-data');
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.id = 'site-structured-data';
      structuredData.type = 'application/ld+json';
      document.head.appendChild(structuredData);
    }
    structuredData.textContent = JSON.stringify(siteJsonLd);
  }, [pathname]);

  return null;
}

export function EventStructuredData({ events }) {
  useEffect(() => {
    const scriptId = 'event-structured-data';
    let script = document.head.querySelector(`#${scriptId}`);

    if (!events.length) {
      script?.remove();
      return undefined;
    }

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': events.map((event) => ({
        '@type': 'Event',
        name: event.name,
        startDate: event.startDate.toISOString().slice(0, 10),
        endDate: event.endDate.toISOString().slice(0, 10),
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location: {
          '@type': 'Place',
          name: typeof event.location === 'string' ? event.location : [event.location?.city, event.location?.state, event.location?.country].filter(Boolean).join(', '),
        },
        url: event.url,
        performer: {
          '@type': 'Organization',
          name: 'Ken & Bryn',
          url: SITE_URL,
        },
      })),
    });

    return () => script?.remove();
  }, [events]);

  return null;
}
